import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAudioRecorder } from 'react-audio-voice-recorder';
import { Button } from '../../components/Button';
import { AudioRecorderDefaultConfig } from '../../components/audioRecorder/AudioRecorder';
import { LiveAudioVisualizer } from '../../components/LiveAudioVisualizer';
import { formatDuration, isSilenceDetectedForGivenTime } from '../../service/MicService';
import { uploadAudioFile } from '../../api/authApis';
import { RootState } from '../../redux/store';
import { convertWebmToWav, getButtonSize } from '../../service/utils';
import { Text } from '../../components/Text';
import useComputedStyle from '../../hooks/useComputedStyle';
import classes from './MicrophoneSelection.module.css';
import { MicObject, ScreenButtonSize } from '../../interface/interface';
import {
    changeFileSpeed,
    changeSpeakPrecheckDuration,
    changePageDetails,
    changeIsSilent,
    changePrecheckApiError,
} from '../../redux/appConfigSlice';

interface SpeakCheckScreenProps {
    selectedMic?: MicObject;
    silenceMeanValue: number;
    setIsSpeakCheckFinished: (val: boolean) => void;
    downLoadSpeed?: string;
    rules: ISpeakCheckRules;
    fontSize: ScreenButtonSize;
}

export interface ISpeakCheckRules {
    silenceBeforeRetry: number;
    maxSpeechTime: number;
    silenceRequiredForComplete: number;
    samplePhrase: string;
}

const SpeakCheckScreen = (props: SpeakCheckScreenProps) => {
    const dispatch = useDispatch();
    const { elementRef, computedStyles } = useComputedStyle();
    const transcriptRef = useRef<string>('');
    const silenceInterval = useRef<NodeJS.Timer>();

    const { testTaker: testTakerDetails, pageObject } = useSelector((state: RootState) => state.appConfigSlice);
    const insperaDetails = useSelector((state: RootState) => state.appConfigSlice.insperaDetails);
    const { rules, fontSize } = props;

    const [isRecordingStarted, setIsRecordingStarted] = useState(false);
    const [errorScreen, setErrorScreen] = useState(false);
    const [uploadBlob, setUploadBlob] = useState(false);
    const [uploadingAudio, setUploadingAudio] = useState<boolean>(false);
    const recordingTime = useRef<number>(0);
    const startTime = useRef<number>(0);

    let audioRecorder = useAudioRecorder({
        ...AudioRecorderDefaultConfig,
        deviceId: props?.selectedMic?.deviceId ?? 'default',
    });

    useEffect(() => {
        dispatch(changeIsSilent(errorScreen));
    }, [errorScreen]);

    useEffect(() => {
        if (audioRecorder?.recordingBlob && uploadBlob) {
            setUploadingAudio(true);
            (async () => {
                try {
                    let { wavBlob } = await convertWebmToWav(audioRecorder.recordingBlob as Blob);
                    let fileFormat = '.wav';
                    let fileName = 'speaking_test';
                    const fileSizeInBytes = wavBlob.size;
                    const startTime = performance.now();
                    const response = await uploadAudioFile(wavBlob, fileFormat, fileName, testTakerDetails, insperaDetails);
                    const endTime = performance.now();
                    const uploadSpeedMbps = (fileSizeInBytes * 8) / ((endTime - startTime) * 1000);
                    console.log(`Upload Speed: ${uploadSpeedMbps.toFixed(2)} Mbps`);
                    let uploadSpeed = `${uploadSpeedMbps.toFixed(2)} Mbps`;
                    if (response) {
                        console.log(response);
                        setUploadingAudio(false);
                        props?.setIsSpeakCheckFinished(true);
                        setIsRecordingStarted(false);
                        dispatch(
                            changeFileSpeed({
                                uploadSpeed: uploadSpeed ?? '',
                                downloadSpeed: props.downLoadSpeed ?? '',
                            })
                        );

                        dispatch(
                            changeSpeakPrecheckDuration({
                                duration: recordingTime.current,
                                responseFilePath: response.data.filePath,
                            })
                        );
                    } else {
                        dispatch(changePrecheckApiError('Error during upload Api'));
                    }
                    dispatch(
                        changePageDetails({
                            ...pageObject,
                            isSpeakCheckDone: true,
                        })
                    );
                } catch (error) {
                    console.log('Error during upload', error);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioRecorder?.recordingBlob]);

    useEffect(() => {
        const fetchData = async () => {
            if (audioRecorder?.mediaRecorder) {
                let silenceTimer = 0,
                    speakingTimer = 0;
                silenceInterval.current = setInterval(async () => {
                    if (!(await isSilenceDetectedForGivenTime(audioRecorder, 500, props?.silenceMeanValue))) {
                        silenceTimer = 0;
                        speakingTimer++;
                    } else {
                        silenceTimer++;
                    }

                    console.log('speaking Timer', speakingTimer);
                    console.log('silence Timer', silenceTimer);

                    if (silenceTimer >= rules.silenceBeforeRetry) {
                        setErrorScreen(true);
                        setUploadBlob(false);
                        onStop();
                        clearInterval(silenceInterval.current);
                    }

                    if (speakingTimer >= rules.maxSpeechTime) {
                        setUploadBlob(true);
                        onStop();
                        clearInterval(silenceInterval.current);
                        setUploadingAudio(true);
                    }

                    if (speakingTimer > 0 && silenceTimer >= rules.silenceRequiredForComplete) {
                        setUploadBlob(true);
                        onStop();
                        clearInterval(silenceInterval.current);
                        setUploadingAudio(true);
                    }
                }, 1000);
            }
        };

        fetchData();

        return () => {
            // Clean up
            transcriptRef.current = '';
            clearInterval(silenceInterval.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioRecorder?.mediaRecorder]);

    const onStart = async () => {
        setIsRecordingStarted(true);
        audioRecorder?.startRecording();
        startTime.current = Date.now();
    };

    const onStop = async () => {
        setIsRecordingStarted(false);
        if (audioRecorder?.mediaRecorder?.state !== 'inactive') {
            audioRecorder?.stopRecording();
        }
        const end = Date.now();
        const elapsed = end - startTime.current;
        recordingTime.current = formatDuration(elapsed, true) as number;
    };

    return (
        <>
            {errorScreen ? (
                <>
                    <p className={classes.topRight}>
                        <Text text={`V207`} fontWeight="medium" variant="body" />
                    </p>
                    <p>
                        <Text text="We can't hear you." />
                    </p>
                    <p>
                        <Text text="This may be because you are speaking too quietly, or because there is too much noise around you." />
                    </p>
                    <p>
                        <Text text="Please move closer to the microphone or try speaking more loudly." />
                    </p>

                    <Button size={getButtonSize(fontSize)} onClick={() => setErrorScreen(false)}>
                        Try Again
                    </Button>
                </>
            ) : (
                <>
                    <p>
                        <Text text="We need to check your microphone." />
                    </p>
                    <p>
                        <Text text="Click <b>Record</b> and read the sentence below." />
                    </p>

                    <Button
                        size={getButtonSize(fontSize)}
                        onClick={onStart}
                        ref={elementRef}
                        locked={isRecordingStarted}
                        id="record"
                        loading={uploadingAudio}
                    >
                        {isRecordingStarted && (
                            <LiveAudioVisualizer
                                mediaRecorder={audioRecorder?.mediaRecorder as MediaRecorder}
                                barColor={computedStyles.color}
                            />
                        )}

                        {uploadingAudio && !isRecordingStarted && 'Uploading'}
                        {!uploadingAudio && !isRecordingStarted && 'Record'}
                    </Button>

                    <p className={classes.sentence}>
                        <Text text={`"${rules.samplePhrase}"`} variant="heading" fontWeight="semibold" />
                    </p>
                </>
            )}
        </>
    );
};

export default SpeakCheckScreen;
